import {
	Box,
	Button,
	CircularProgress,
	InputAdornment,
	TextField,
} from '@mui/material'

import { RequestStatus } from '../../types/Work/Request'
import ConfirmDialog from '../core/ConfirmDialog'
import React, { useRef, useState } from 'react'
import { updateInspectionRequest } from '../../services/inspectionsService'
import { useQCContext } from './QCContext'
import { CameraAlt, PhotoLibrary, PlaylistAddCheckCircle } from '@mui/icons-material'
import { ImageSlider } from '../core/ImageSlider'

type ActionDialogProps = {
	visible: boolean
	passOrRework: RequestStatus
	setVisible: (arg: boolean) => void
	loadParentData?: () => Promise<void>
	taskType: string
}

export function ActionDialog({
	visible,
	passOrRework,
	setVisible,
	loadParentData,
	taskType,
}: ActionDialogProps) {
	const [inspectionComment, setInspectionComment] = useState('')
	const [isFinalInspection, setIsFinalInspection] = useState(false)
	const [progress, setProgress] = useState<number>()
	const [submiting, setSubmiting] = useState(false)
	const [blobs, setBlobs] = useState<Blob[]>([])
	const cameraInputRef = useRef<HTMLInputElement>(null)
	const galleryInputRef = useRef<HTMLInputElement>(null)
	const { request, task, lastLayerWorkItem } = useQCContext()

	const handleOpenCamera = () => {
		if (cameraInputRef.current) {
			cameraInputRef.current.value = ''
			cameraInputRef.current.click()
		}
	}

	const handleCameraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (file?.type.startsWith('image/')) {
			setBlobs((prev) => [...prev, file])
		}
		if (cameraInputRef.current) {
			cameraInputRef.current.value = ''
		}
	}

	const handleOpenGallery = () => {
		galleryInputRef.current?.click()
	}

	const handleGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files
		if (files?.length) {
			const newBlobs = Array.from(files).filter((file) => file.type.startsWith('image/'))
			setBlobs((prev) => [...prev, ...newBlobs])
		}
		if (galleryInputRef.current) {
			galleryInputRef.current.value = ''
		}
	}

	const handleRemoveAttachment = (index: number) => {
		setBlobs((prev) => prev.filter((_, i) => i !== index))
	}

	const clearBlobs = () => {
		setBlobs([])
	}

	const handleClose = () => {
		setVisible(false)
		setInspectionComment('')
		clearBlobs()
		setSubmiting(false)
	}

	return (
		<>
			<input
				ref={cameraInputRef}
				type='file'
				accept='image/*'
				capture='environment'
				style={{ display: 'none' }}
				onChange={handleCameraChange}
			/>
			<input
				ref={galleryInputRef}
				type='file'
				accept='image/*'
				multiple
				style={{ display: 'none' }}
				onChange={handleGalleryChange}
			/>
			<ConfirmDialog
				visible={visible}
				title={`${passOrRework} ${task?.name}${
					passOrRework === 'Passed'
						? ` (${
								(task?.quantityComplete ?? 0) - (request?.inspectedQuantity ?? 0)
						  } ${lastLayerWorkItem?.unitOfMeasure} Remaining)`
						: ''
				}`}
				cancelButtonText='Cancel'
				confirmButton={submiting ? <CircularProgress color='success' sx={{ m: 0 }} /> : undefined}
				confirmButtonText='Submit'
				onCancel={() => {
					handleClose()
				}}
				onConfirm={async () => {
					setSubmiting(true)
					if (request) {
						try {
							const res = await updateInspectionRequest(
								request.id,
								blobs,
								inspectionComment,
								isFinalInspection,
								passOrRework,
								progress,
								taskType,
							)
							if (res) {
								loadParentData && (await loadParentData())
								handleClose()
							}
						} finally {
							setSubmiting(false)
						}
					}
				}}
				canConfirm={true}
				childActions={
					<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<Button
							size='small'
							variant='contained'
							color='primary'
							onClick={handleOpenCamera}
							startIcon={<CameraAlt />}
						>
							Open Camera
						</Button>
						<Button
							size='small'
							variant='outlined'
							color='primary'
							onClick={handleOpenGallery}
							startIcon={<PhotoLibrary />}
							sx={{ color: 'text.primary' }}
						>
							Attach from gallery
						</Button>
					</div>
				}
			>
				<Box sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
					{passOrRework === 'Passed' && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
							<TextField
								margin='normal'
								type='number'
								size='small'
								value={progress}
								label='Quantity Inspected'
								onChange={(e) => setProgress(parseFloat(e.target.value))}
								inputProps={{
									min: 0,
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position='start'>
											{request?.inspectedQuantity} {lastLayerWorkItem?.unitOfMeasure} +
										</InputAdornment>
									),
								}}
								sx={{ width: '70%' }}
							/>
							<Button
								variant='contained'
								size='small'
								onClick={() =>
									setProgress(
										(task?.quantityComplete ?? 0) - (request?.inspectedQuantity ?? 0),
									)
								}
								startIcon={<PlaylistAddCheckCircle />}
								sx={{
									width: '30%',
									height: '40px', // Reduced height to match TextField
									mt: 1, // Match TextField margin-top
								}}
							>
								Claim Remaining
							</Button>
						</Box>
					)}
					<TextField
						onChange={(e) => setInspectionComment(e.target.value)}
						size='medium'
						label={'Inspection Comment'}
						multiline
						rows={4}
						value={inspectionComment}
						autoComplete='off'
						sx={{ bgcolor: '#E3F2FD', minWidth: '60ch' }}
						placeholder={'enter inspection comment here '}
						inputProps={{ maxLength: 255 }}
					/>
					<div style={{ marginLeft: '8px', fontSize: '8pt' }}>
						{inspectionComment.length} of 255 characters.
					</div>

					{blobs.length > 0 && (
						<Box sx={{ mt: 2 }}>
							<ImageSlider
								blobs={blobs}
								setBlobs={setBlobs}
								handleDelete={handleRemoveAttachment}
								readOnly={false}
							/>
						</Box>
					)}
				</Box>
			</ConfirmDialog>
		</>
	)
}
